import {
  faArrowRight,
  faCalendarAlt,
  faUsers,
  faVideo,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components/macro";
import {
  CardTitleIcon,
  CTAButton,
  PanelCard,
  SecondaryCTAButton,
} from "../shared/styles";
import { analytics, PANEL_EVENTS } from "../utils/analytics";
import CoachProfileModule from "./CoachProfileModule";

// ─── Styled components ────────────────────────────────────────────────────────

const Spacer = styled.div`
  height: ${({ theme }) => theme.spacing.md};
`;

const StatusText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.body};
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  line-height: 1.5;
`;

const SessionBox = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const SessionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const SessionLabel = styled.p`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.heading};
  margin: 0 0 2px;
`;

const SessionDate = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.body};
  margin: 2px 0 0;
`;

const JoinSessionText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const ManagerCard = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.primaryBackground};
  padding: ${({ theme }) => theme.spacing.md};
  box-shadow: ${({ theme }) => theme.boxShadow.md};
  border-top: 2px solid ${({ theme }) => theme.colors.heroPink};
`;

const ManagerCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ManagerIconCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.altBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.heading};
  font-size: 14px;
`;

const ManagerCardTitle = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.heading};
  margin: 0 0 2px;
`;

const ManagerCardSubtitle = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.body};
  margin: 0;
`;

const ManagerStats = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const ManagerStatRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
`;

const StatLabel = styled.span`
  color: ${({ theme }) => theme.colors.body};
`;

const StatBadgeAlert = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.errorRed};
`;

const StatBadge = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.heading};
`;

const ManagerCTAButton = styled(Link)`
  margin-top: ${({ theme }) => theme.spacing.md};
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.heroPink};
  padding: 10px 0;
  font-size: 14px;
  font-weight: 500;
  color: white;
  text-decoration: none;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Formats ISO date + time + timezone into "Thu, Jan 25 at 10:00 AM EST"
 * Converts to user's local timezone for display
 */
function formatSessionDate(dateStr, time, timezone) {
  if (!dateStr) return "";
  try {
    // Combine date, time and timezone into a single parseable string
    // e.g. "2026-01-10 10:00 AM EST"
    const dateTimeString = `${dateStr.split(" ")[0]} ${time} ${timezone}`;
    const sessionDate = new Date(dateTimeString);

    // Format in user's local timezone
    const day = sessionDate.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    /* const localTimezone = sessionDate
      .toLocaleTimeString("en-AU", { timeZoneName: "short" })
      .split(" ")
      .pop(); */

    return `${day}`.trim();
  } catch {
    return [dateStr, time, timezone].filter(Boolean).join(" ");
  }
}

/**
 * Formats ISO date + time + timezone into "Thu, Jan 25 at 10:00 AM EST"
 * Converts to user's local timezone for display
 */
function formatSessionTime(dateStr, time, timezone) {
  if (!dateStr) return "";
  try {
    // Combine date, time and timezone into a single parseable string
    // e.g. "2026-01-10 10:00 AM EST"
    const dateTimeString = `${dateStr.split(" ")[0]} ${time} ${timezone}`;
    const sessionDate = new Date(dateTimeString);

    // Format in user's local timezone

    const localTime = sessionDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });

    return localTime.trim();
  } catch {
    return [dateStr, time, timezone].filter(Boolean).join(" ");
  }
}

// ─── Individual coaching status ───────────────────────────────────────────────
function IndividualCoachingStatus({
  nextIndividualSession,
  onBookingAction,
  ownSessions,
  sessionTitle,
}) {
  // Case 0: all sessions are completed, as there is no next session to book or booked → show CTA to explore other resources
  if (!nextIndividualSession && ownSessions?.length) {
    return (
      <>
        <StatusText>You've completed your coaching journey.</StatusText>
        <CTAButton
          href="/explore"
          onClick={() => {
            analytics.track(PANEL_EVENTS.BOOKING_CTA_CLICKED, {
              context: "completed",
            });
            onBookingAction?.("explore");
          }}
        >
          Explore tools and resources
        </CTAButton>
      </>
    );
  }
  // Case 1: session booked → show next session date/time + join link
  if (nextIndividualSession?.status.toLowerCase() === "booked") {
    const dateLabel = formatSessionDate(
      nextIndividualSession.date,
      nextIndividualSession.time,
      nextIndividualSession.timezone
    );
    const timeLabel = formatSessionTime(
      nextIndividualSession.date,
      nextIndividualSession.time,
      nextIndividualSession.timezone
    );
    return (
      <>
        <SessionBox>
          {sessionTitle && (
            <SessionRow>
              <b>{sessionTitle}</b>
            </SessionRow>
          )}
          <SessionRow>
            <CardTitleIcon>
              <FontAwesomeIcon icon={faCalendarAlt} />
            </CardTitleIcon>
            <div>
              <SessionLabel>Next session</SessionLabel>
              <SessionDate>{dateLabel}</SessionDate>
              <SessionDate>{timeLabel}</SessionDate>
            </div>
          </SessionRow>
          {nextIndividualSession.vcLink && (
            <CTAButton
              onClick={() => {
                analytics.track(PANEL_EVENTS.RESCHEDULE_CTA_CLICKED, {
                  session_id: nextIndividualSession.id,
                });
                onBookingAction?.("manage");
              }}
              href={nextIndividualSession.vcLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <JoinSessionText>
                <FontAwesomeIcon size="sm" icon={faVideo} />
                Join Session
              </JoinSessionText>
            </CTAButton>
          )}
        </SessionBox>

        <SecondaryCTAButton
          href={
            process.env.REACT_APP_COACH_CONNECT_URL +
            "/session/" +
            nextIndividualSession.id
          }
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            analytics.track(PANEL_EVENTS.BOOKING_CTA_CLICKED, {
              session_id: nextIndividualSession.id,
              context: "vcLink",
            })
          }
        >
          Manage Session
        </SecondaryCTAButton>
      </>
    );
  }
  // Case 2: no first session booked yet
  if (nextIndividualSession?.name.toLowerCase().includes("session 1")) {
    return (
      <>
        <StatusText>
          You’ve been allocated coaching by your organisation. Your coach is
          ready when you are.
        </StatusText>
        <CTAButton
          href={
            process.env.REACT_APP_COACH_CONNECT_URL +
            "/session/" +
            nextIndividualSession.id
          }
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            analytics.track(PANEL_EVENTS.BOOKING_CTA_CLICKED, {
              context: "book_first",
            });
            onBookingAction?.("book_first");
          }}
        >
          Book your first session
        </CTAButton>
      </>
    );
  }
  // Case 3: first session done but next not yet booked
  if (nextIndividualSession?.status.toLowerCase() !== "booked") {
    return (
      <>
        <StatusText>
          Continue your coaching journey with your dedicated coach.
        </StatusText>
        <CTAButton
          href={
            process.env.REACT_APP_COACH_CONNECT_URL +
            "/session/" +
            nextIndividualSession.id
          }
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            analytics.track(PANEL_EVENTS.BOOKING_CTA_CLICKED, {
              context: "book_next",
            });
            onBookingAction?.("book_next");
          }}
        >
          Book your next session
        </CTAButton>
      </>
    );
  }

  return null;
}

// ─── Manager coaching status ──────────────────────────────────────────────────

/**
 * Returns active sessions: open package only, excluding cancelled/no-show.
 * Edge: previous package done + new one allocated → only consider open package.
 */
function getActiveSessions(sessions = []) {
  return sessions.filter(
    (s) => !["completed", "no_show"].includes(s.status.toLowerCase())
  );
}

function ManagerCoachingStatus({ employeeSessions }) {
  const activeSessions = getActiveSessions(employeeSessions);
  const needsBooking = activeSessions.filter(
    (s) => s.status.toLowerCase() !== "booked"
  ).length;

  const now = new Date();
  const weekFromNow = new Date(now);
  weekFromNow.setDate(now.getDate() + 7);
  const upcomingThisWeek = activeSessions.filter((s) => {
    if (s.status.toLowerCase() !== "booked" || !s.date) return false;
    const d = new Date(s.date.split(" ")[0]);
    return d >= now && d <= weekFromNow;
  }).length;

  return (
    <ManagerCard>
      <ManagerCardHeader>
        <ManagerIconCircle>
          <FontAwesomeIcon icon={faUsers} />
        </ManagerIconCircle>
        <div>
          <ManagerCardTitle>Your Team&apos;s Coaching</ManagerCardTitle>
          <ManagerCardSubtitle>
            {activeSessions.length} active{" "}
            {activeSessions.length === 1 ? "journey" : "journeys"}
          </ManagerCardSubtitle>
        </div>
      </ManagerCardHeader>
      <ManagerStats>
        <ManagerStatRow>
          <StatLabel>Needs booking</StatLabel>
          <StatBadgeAlert>{needsBooking}</StatBadgeAlert>
        </ManagerStatRow>
        <ManagerStatRow>
          <StatLabel>Upcoming this week</StatLabel>
          <StatBadge>{upcomingThisWeek}</StatBadge>
        </ManagerStatRow>
      </ManagerStats>
      <ManagerCTAButton to="/manage-sessions">
        Manage coaching sessions
        <FontAwesomeIcon icon={faArrowRight} size="sm" />
      </ManagerCTAButton>
    </ManagerCard>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

/**
 * GPDS-903 — Coaching area status + CTA logic
 * Routes to individual or manager layout based on `role`.
 * Handles all edge cases: cancelled/no-show (ignored), package boundaries,
 * manager with own PL coaching.
 */
export default function CoachingStatusModule({
  role,
  ownSessions,
  employeeSessions,
  nextIndividualSession = {},
  onBookingAction,
  sessionTitle = "",
}) {
  return (
    <>
      {employeeSessions?.length > 0 && (
        <ManagerCoachingStatus
          key="managerSession"
          employeeSessions={employeeSessions}
        />
      )}

      {employeeSessions?.length > 0 && nextIndividualSession?.id && <Spacer />}

      {ownSessions?.length > 0 && (
        <PanelCard>
          <IndividualCoachingStatus
            key="ownSession"
            ownSessions={ownSessions}
            nextIndividualSession={nextIndividualSession}
            onBookingAction={onBookingAction}
            sessionTitle={sessionTitle}
          />
        </PanelCard>
      )}

      {ownSessions?.length > 0 &&
        nextIndividualSession?.coach &&
        !employeeSessions?.length && ( //no coach for manager
          <>
            <CoachProfileModule coach={nextIndividualSession.coach} />
          </>
        )}
    </>
  );
}
